import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import SessionCard from "@/components/session-card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

export default function SessionsPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  // Check for booking success query param
  const query = new URLSearchParams(location.split("?")[1] || "");
  const bookingSuccess = query.get("booking") === "success";
  const therapistId = query.get("therapistId");
  const sessionDate = query.get("date");
  const isFree = query.get("isFree") === "true";
  
  // Create session if booking success
  const createSession = async () => {
    if (!bookingSuccess || !therapistId || !sessionDate) return;
    
    try {
      await apiRequest("POST", "/api/sessions", {
        therapistId: parseInt(therapistId),
        date: sessionDate ? new Date(sessionDate).toISOString() : null,
        isFree: isFree,
        status: "scheduled",
      });
      
      // Invalidate sessions query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      
      toast({
        title: "Sessão agendada com sucesso",
        description: "Sua sessão foi agendada e aparecerá na lista de sessões.",
      });
      
      // Remove query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      toast({
        title: "Erro ao agendar sessão",
        description: "Não foi possível agendar a sessão. Tente novamente.",
        variant: "destructive",
      });
    }
  };
  
  // Fetch sessions
  const { data: sessions, isLoading } = useQuery({
    queryKey: ["/api/sessions"],
    onSuccess: () => {
      // If we have booking success params, create the session
      if (bookingSuccess) {
        createSession();
      }
    }
  });
  
  // Filter sessions by status
  const filteredSessions = sessions ? sessions.filter((session: any) => {
    if (statusFilter === "all") return true;
    return session.status === statusFilter;
  }) : [];
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow bg-neutral">
        <section className="container mx-auto px-4 py-8 md:py-12">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold">Minhas Sessões</h1>
              <p className="text-gray-600 mt-1">Histórico e agendamentos</p>
            </div>
            <div>
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="scheduled">Agendadas</SelectItem>
                  <SelectItem value="completed">Realizadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Card className="bg-white rounded-lg shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredSessions.length === 0 ? (
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Nenhuma sessão encontrada.</p>
                <Button
                  className="mt-4 bg-primary hover:bg-secondary"
                  onClick={() => window.location.href = "/booking"}
                >
                  Agendar uma sessão
                </Button>
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Terapeuta
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data e Hora
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSessions.map((session: any) => (
                      <SessionCard key={session.id} session={session} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
